/**
 * Phase 11 — subscribes to OpenClaw's auto-emitted `session.tool`
 * EventFrame and dispatches matching tool calls to the platform-ops
 * registry.
 *
 * **Thin-slice X.1.3 wire.** OpenClaw core emits `session.tool`
 * EventFrames whenever the agent invokes any tool ([server-chat.ts:666])
 * — payload shape:
 *
 *   {
 *     runId, seq, stream, ts, sessionKey,
 *     data: { phase, toolName, toolInput, ... }
 *   }
 *
 * We listen for `phase === "start"` events whose `toolName` matches a
 * registered platform-ops op (`ide.*` for the thin slice). Dispatch to
 * the registry; log the result. The result currently doesn't flow back
 * to the runtime (fire-and-forget per the thin-slice design — see
 * guide §4.5.2 + the X.2 work for bidirectional correlation via custom
 * `platform_ops.op_request` / `platform_ops.op_result` events).
 *
 * The handler is exported separately (`handleSessionToolEvent`) so
 * vitest can drive it with synthetic payloads without mounting a
 * React tree — see `usePlatformOpListener.test.ts`.
 */
import { useMemo } from "react";
import { useEventStream } from "@/hooks/useEventStream";
import { platformOpsRegistry, type HandlerContext } from "@/lib/platform-ops-registry";
import type { PlatformOp } from "@lexiun/lx-platform-ops";

interface SessionToolEventPayload {
  runId?: string;
  sessionKey?: string;
  data?: {
    phase?: string;
    // Live wire carries pi-style `name`/`args` (server-chat toolPayload);
    // `toolName`/`toolInput` kept for the rekeyed variants.
    name?: string;
    args?: Record<string, unknown>;
    toolName?: string;
    toolInput?: Record<string, unknown>;
    toolCallId?: string;
  };
}

interface UsePlatformOpListenerOptions {
  catalogOps: readonly PlatformOp[] | undefined;
}

/**
 * Pure dispatcher: given a catalog (as a Map by name+aliases) and a
 * raw EventFrame payload, decide whether to dispatch and to which
 * handler. Returns a Promise of the dispatch outcome, or `null` if
 * the event was filtered out (wrong phase / non-ide_client / unknown
 * tool / no catalog).
 *
 * Exported so vitest specs can drive it with synthetic payloads.
 */
export function handleSessionToolEvent(
  rawPayload: unknown,
  opsByName: ReadonlyMap<string, PlatformOp>,
): Promise<{ ok: boolean; error?: { code: string; message: string } }> | null {
  const payload = rawPayload as SessionToolEventPayload;
  const phase = payload.data?.phase;
  const toolName = payload.data?.name ?? payload.data?.toolName;
  const toolInput = payload.data?.args ?? payload.data?.toolInput;

  if (phase !== "start") return null;
  if (typeof toolName !== "string") return null;

  const op = opsByName.get(toolName);
  if (!op || op.target !== "ide_client") return null;

  const ctx: HandlerContext = {
    op,
    toolCallId: payload.data?.toolCallId,
    sessionKey: payload.sessionKey,
  };

  return Promise.resolve(
    platformOpsRegistry.dispatch(op.name, toolInput ?? {}, ctx),
  ).then((result) => {
    if (!result.ok) {
      // eslint-disable-next-line no-console
      console.warn(
        `[platform-ops] handler dispatch for "${op.name}" failed:`,
        result.error,
      );
    }
    return result;
  });
}

/** Build the name → op lookup including aliases. */
export function buildOpsByName(
  catalogOps: readonly PlatformOp[] | undefined,
): Map<string, PlatformOp> {
  const map = new Map<string, PlatformOp>();
  if (!catalogOps) return map;
  for (const op of catalogOps) {
    map.set(op.name, op);
    for (const alias of op.aliases) map.set(alias, op);
  }
  return map;
}

export function usePlatformOpListener({ catalogOps }: UsePlatformOpListenerOptions): void {
  const opsByName = useMemo(() => buildOpsByName(catalogOps), [catalogOps]);

  useEventStream("session.tool", (rawPayload) => {
    // Pure dispatch — fire-and-forget. Result is logged inside.
    // Skips cleanly when the event is filtered out (returns null).
    const pending = handleSessionToolEvent(rawPayload, opsByName);
    if (pending) void pending;
  });
}
